import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

const getBulkDeleteSkillErrorMessage = (error, { hasChildren = false } = {}) => {
  if (hasChildren) {
    return "Some selected skills can’t be deleted because they have sub-skills. Delete/move the sub-skills first.";
  }

  const status = error?.response?.status;
  const data = error?.response?.data;

  const rawMessage =
    (typeof data === "string" && data) ||
    data?.message ||
    data?.error ||
    data?.detail ||
    error?.message;

  const normalized = String(rawMessage || "").toLowerCase();

  const looksLikeHasChildren =
    normalized.includes("sub") ||
    normalized.includes("child") ||
    normalized.includes("children") ||
    normalized.includes("linked") ||
    normalized.includes("dependent") ||
    normalized.includes("constraint") ||
    normalized.includes("foreign key");

  if (status === 409 || (status === 500 && looksLikeHasChildren) || looksLikeHasChildren) {
    return "Some selected skills can’t be deleted because they have sub-skills or are linked to other items.";
  }

  if (status === 403) {
    return "You don’t have permission to delete these skills.";
  }

  if (rawMessage) return String(rawMessage);

  return "Failed to delete skills. Please try again.";
};

export default function DeleteSkillsBulkDialog({
  open,
  onOpenChange,
  skillIds,
  skills,
  onDeleted,
}) {
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();

  const selectedIdSet = useMemo(
    () => new Set((Array.isArray(skillIds) ? skillIds : []).map((id) => String(id))),
    [skillIds]
  );

  const selectedSkills = useMemo(() => {
    const list = Array.isArray(skills) ? skills : [];
    return list.filter((skill) => selectedIdSet.has(String(skill?.id)));
  }, [skills, selectedIdSet]);

  const hasChildren = useMemo(() => {
    if (selectedSkills.length === 0) return false;
    const list = Array.isArray(skills) ? skills : [];
    const selectedIds = new Set(selectedSkills.map((s) => s?.id));

    return list.some((skill) => {
      const parentId = skill?.parent_skill_id ?? null;
      return parentId != null && selectedIds.has(parentId);
    });
  }, [skills, selectedSkills]);

  const title = "Delete Skills";
  const count = selectedIdSet.size;

  const handleDelete = async () => {
    if (count === 0) return;

    if (hasChildren) {
      toast.error(
        "Some selected skills can’t be deleted because they have sub-skills. Delete/move the sub-skills first."
      );
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: {
          skill_ids: Array.from(selectedIdSet).map((id) => {
            const maybeNum = Number(id);
            return Number.isFinite(maybeNum) ? maybeNum : id;
          }),
        },
      };

      const res = await axios.delete(
        api_endpoint + "/course/skill/delete/bulk",
        reqConfig
      );

      const deletedCount =
        Number(res?.data?.deleted_count ?? res?.data?.deletedCount) || count;

      toast.success(`Deleted ${deletedCount} skill${deletedCount === 1 ? "" : "s"}`);
      onOpenChange?.(false);
      if (onDeleted) onDeleted(res?.data);
    } catch (error) {
      toast.error(getBulkDeleteSkillErrorMessage(error, { hasChildren }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="font-body text-(--neutral-900)">
          Are you sure you want to delete <b>{count}</b> skill{count === 1 ? "" : "s"}?
          This action cannot be undone.
        </div>

        {hasChildren ? (
          <div className="mt-3 rounded-md bg-(--neutral-100) p-3 text-sm text-(--neutral-900)">
            Some selected skills have sub-skills. Delete/move the sub-skills first, then try again.
          </div>
        ) : null}

        <DialogFooter className="">
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={loading}
              className=""
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant=""
            onClick={handleDelete}
            disabled={loading || hasChildren || count === 0}
            className=""
          >
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
